import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  CalendarFeedStatus, CalendarSubscription, DelegateCandidate, DelegationView, HomeView,
  OutOfOfficeView, Team,
} from '../types'

/**
 * One call for the whole portal.
 *
 * <p>Eight cards from eight endpoints would be eight spinners on a phone, and eight chances for
 * one slow query to make the page look broken.
 */
export function useEssHome() {
  return useQuery({
    queryKey: ['ess', 'home'],
    queryFn: async () => (await apiClient.get<HomeView>('/v1/ess/home')).data,
  })
}

// ------------------------------------------ my team, delegation, calendar (Phase 9.6)

/** The reporting line as of today, with who is off and who is off next. */
export function useMyTeam() {
  return useQuery({
    queryKey: ['ess', 'my-team'],
    queryFn: async () => (await apiClient.get<Team>('/v1/ess/my-team')).data,
  })
}

export function useDelegations() {
  return useQuery({
    queryKey: ['ess', 'delegations'],
    queryFn: async () =>
      (await apiClient.get<DelegationView[]>('/v1/ess/delegation')).data,
  })
}

export function useDelegationsToMe() {
  return useQuery({
    queryKey: ['ess', 'delegations', 'to-me'],
    queryFn: async () =>
      (await apiClient.get<DelegationView[]>('/v1/ess/delegation/to-me')).data,
  })
}

/** Who the caller could delegate to: name and id, and nothing an employee should not see. */
export function useDelegateCandidates() {
  return useQuery({
    queryKey: ['ess', 'delegation', 'candidates'],
    queryFn: async () =>
      (await apiClient.get<DelegateCandidate[]>('/v1/ess/delegation/candidates')).data,
  })
}

export function useOutOfOffice() {
  return useQuery({
    queryKey: ['ess', 'out-of-office'],
    queryFn: async () =>
      (await apiClient.get<OutOfOfficeView>('/v1/ess/delegation/out-of-office')).data,
  })
}

export function useDelegationMutations() {
  const queryClient = useQueryClient()
  // Delegating sets the away dates, so both lists move together and both are refreshed.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ess'] })

  const delegate = useMutation({
    mutationFn: async (body: {
      toUserId: string; fromDate: string; toDate: string
      entityKey: string | null; note: string | null
    }) => (await apiClient.post<DelegationView>('/v1/ess/delegation', body)).data,
    onSuccess: invalidate,
  })

  const cancel = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.delete<DelegationView>(`/v1/ess/delegation/${id}`)).data,
    onSuccess: invalidate,
  })

  const setAway = useMutation({
    mutationFn: async (body: { fromDate: string; toDate: string; note: string | null }) =>
      (await apiClient.put<OutOfOfficeView>('/v1/ess/delegation/out-of-office', body)).data,
    onSuccess: invalidate,
  })

  const clearAway = useMutation({
    mutationFn: async () =>
      (await apiClient.delete<OutOfOfficeView>('/v1/ess/delegation/out-of-office')).data,
    onSuccess: invalidate,
  })

  return { delegate, cancel, setAway, clearAway }
}

export function useCalendarFeed() {
  return useQuery({
    queryKey: ['ess', 'calendar-feed'],
    queryFn: async () =>
      (await apiClient.get<CalendarFeedStatus>('/v1/ess/calendar-feed')).data,
  })
}

export function useCalendarFeedMutations() {
  const queryClient = useQueryClient()
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['ess', 'calendar-feed'] })

  const issue = useMutation({
    mutationFn: async () =>
      (await apiClient.post<CalendarSubscription>('/v1/ess/calendar-feed')).data,
    onSuccess: invalidate,
  })

  const revoke = useMutation({
    mutationFn: async () =>
      (await apiClient.delete<CalendarFeedStatus>('/v1/ess/calendar-feed')).data,
    onSuccess: invalidate,
  })

  return { issue, revoke }
}
